import React from "react";
import moment from "moment";

import connectToStores from "alt-utils/lib/connectToStores";
import EventBucketStore from "../stores/EventBucketStore";
import EventBucketActions from "../actions/EventBucketActions";
import AnalyticsStore from "../stores/AnalyticsStore";
import AnalyticsActions from "../actions/AnalyticsActions";
import EventStore from "../stores/EventStore";
import EventActions from "../actions/EventActions";
import VenueStore from "../stores/VenueStore";
import VenueActions from "../actions/VenueActions";
import TagStore from "../stores/TagStore";
import TagActions from "../actions/TagActions";

import AnalyticsHeader from "./partials/analytics-header.jsx";
import WorkingIndicator from "./partials/working-indicator.jsx";

import StatsBlock from "./partials/stats-block.jsx";

import EventSalesAnalyticsTable from "./partials/event-sales-analytics-table.jsx";
import AnalyticsFilterWidget from "./partials/analytics-filter-widget.jsx";
import FromCacheCallout from "./partials/from-cache-callout.jsx";

import { formatPrice } from "../util";

class EventSalesAnalytics extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showRangeEditor: false,
      allTime: false,
      range: {
        key: "selection",
        startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
      },
    };

    this.handleDateChange = this.handleDateChange.bind(this);
  }

  static getStores() {
    return [EventBucketStore, EventStore, VenueStore, TagStore, AnalyticsStore];
  }

  static getPropsFromStores() {
    const eventState = EventStore.getState();
    const venueState = VenueStore.getState();
    const tagState = TagStore.getState();

    return {
      getEventBuckets: EventBucketStore.getEventBuckets,
      getEventBucketById: EventBucketStore.getEventBucketById,
      events: eventState.events,
      venues: venueState.venues,
      tags: tagState.tags,
      isFetching:
        eventState.isFetching || venueState.isFetching || tagState.isFetching,
      getEventsForEventBucket: EventStore.getEventsForEventBucket,
      getEventSalesAnalytics: AnalyticsStore.getEventSalesAnalytics
    };
  }

  static contextTypes = {
    router: React.PropTypes.object
  };

  handleDateChange({ range, allTime }) {
    this.setState({ range: range, allTime: allTime },
      () => this.componentWillReceiveProps(this.props, true)
    );
  }

  componentDidMount() {
    this.componentWillReceiveProps(this.props, true);
  }

  componentWillUnmount() {
    AnalyticsActions.clearFetchEventSalesAnalyticsInterval();
  }

  componentWillReceiveProps(newProps, force = false) {
    if (
      force === true ||
      newProps.params.eventBucketId !== this.props.params.eventBucketId
    ) {
      AnalyticsActions.fetchEventSalesAnalytics.defer({
        eventBucketId: newProps.params.eventBucketId,
        startDate: !this.state.allTime ? JSON.stringify(this.state.range.startDate) : null,
        endDate: !this.state.allTime ? JSON.stringify(this.state.range.endDate) : null,
        allTime: this.state.allTime
      });
      EventActions.fetchAllForBucket.defer(newProps.params.eventBucketId);
      VenueActions.fetchAllForBucket.defer(newProps.params.eventBucketId);
      TagActions.fetchAllForBucket.defer(newProps.params.eventBucketId);
      EventBucketActions.fetchOne.defer(newProps.params.eventBucketId);
    }
  }

  render() {
    const { eventBucketId } = this.props.params;
    const eventBucket = this.props.getEventBucketById(eventBucketId);
    const { events, tags, venues, isFetching } = this.props;
    const eventBucketEvents = this.props.getEventsForEventBucket(eventBucketId);
    const eventSalesAnalytics = this.props.getEventSalesAnalytics(eventBucketId);
    const defaultItemsPerPage = 25;

    if (
      !eventSalesAnalytics ||
      !eventBucket ||
      (eventBucketEvents.length === 0 && isFetching)
    ) {
      return <WorkingIndicator fullPage={true} />;
    }

    let timePeriod = this.state.allTime
      ? "All time"
      : moment(this.state.range.startDate)
        .twix(this.state.range.endDate, { allDay: true })
        .format({ implicitYear: false });

    return (
      <div>
        <AnalyticsHeader
          eventBucket={eventBucket}
          allEventBuckets={this.props.getEventBuckets()}
          label="standalone ticket analytics"
          subpath="event_sales"
        />
        <FromCacheCallout analytics={eventSalesAnalytics} />
        <AnalyticsFilterWidget
          showDateFilter={true}
          onDateRangeChange={this.handleDateChange}
        />
        <p>
          Scroll down to view standalone ticket sales{" "}
          <a href="#by-event">by event</a>, <a href="#by-venue">by venue</a>,
          and <a href="#by-tag">by tag</a>.
        </p>
        {this.props.isFetching
          || !eventSalesAnalytics
          || !eventBucket
          ? (
            <div><WorkingIndicator fullPage={true} /></div>
          ) : (
            <div>
              <StatsBlock
                value={formatPrice(eventSalesAnalytics.total_volume)}
                label="Total standalone ticket sales volume"
              />
              <a className="anchor--fixed_header_offset" id="by-event" />
              <div className="analytics-element">
                <h2 style={{ marginTop: "1em", marginBottom: "0.5em" }}>
                  Standalone ticket sales by event
                </h2>
                <EventSalesAnalyticsTable
                  eventBucket={eventBucket}
                  events={events}
                  data={eventSalesAnalytics.volumes}
                  itemsPerPage={defaultItemsPerPage}
                />
              </div>
              <a className="anchor--fixed_header_offset" id="by-venue" />
              <div className="analytics-element">
                <h2 style={{ marginTop: "1em", marginBottom: "0.5em" }}>
                  Standalone ticket sales by venue
                </h2>
                <EventSalesAnalyticsTable
                  eventBucket={eventBucket}
                  venues={venues}
                  events={events}
                  groupBy="venue"
                  data={eventSalesAnalytics.volumes}
                  itemsPerPage={defaultItemsPerPage}
                />
              </div>
              <a className="anchor--fixed_header_offset" id="by-tag" />
              <div className="analytics-element">
                <h2 style={{ marginTop: "1em", marginBottom: "0.5em" }}>
                  Standalone ticket sales by tag
                </h2>
                <EventSalesAnalyticsTable
                  eventBucket={eventBucket}
                  tags={tags}
                  events={events}
                  groupBy="tag"
                  data={eventSalesAnalytics.volumes}
                  itemsPerPage={defaultItemsPerPage}
                />
              </div>
            </div>
          )}
      </div>
    );
  }
}

export default connectToStores(EventSalesAnalytics);



// WEBPACK FOOTER //
// ./src/app/components/event-sales-analytics.jsx